/**
 * AI Configuration Panel Component
 * =================================
 * Settings panel for AI configuration and escalation rules
 */

import React, { useState, useEffect } from 'react';
import { Brain, Shield, Settings2, Mic, Save, Loader2, Bot } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/use-toast';
import api from '../../services/api';

interface AIConfig {
  llm_provider: 'ollama' | 'openai' | 'groq' | 'anthropic';
  llm_model: string;
  llm_base_url?: string;
  system_prompt?: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  enable_rag: boolean;
  enable_mcp: boolean;
  enable_streaming: boolean;
  enable_thinking_mode: boolean;
  response_format: 'markdown' | 'plain' | 'html';
  max_response_length: number;
  api_key_configured: boolean;
}

interface EscalationRules {
  auto_escalation_enabled: boolean;
  confidence_threshold: number;
  sentiment_threshold: number;
  repetition_threshold: number;
  min_response_chars: number;
  max_ai_interactions: number;
  enable_sentiment_escalation: boolean;
  enable_confidence_escalation: boolean;
  enable_repetition_escalation: boolean;
  enable_timeout_escalation: boolean;
  enable_explicit_request_escalation: boolean;
  default_assignment_type: 'round_robin' | 'manual' | 'load_based';
  notify_agents: boolean;
}

interface STTConfig {
  enabled: boolean;
  provider: 'openai' | 'groq' | 'whisper' | 'ollama' | 'transformers' | 'anthropic';
  openai_api_key?: string;
  groq_api_key?: string;
  anthropic_api_key?: string;
  whisper_model?: string;
  ollama_model?: string;
  transformers_model?: string;
}

export const AIConfigPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiConfig, setAIConfig] = useState<AIConfig | null>(null);
  const [escalationRules, setEscalationRules] = useState<EscalationRules | null>(null);
  const [sttConfig, setSTTConfig] = useState<STTConfig>({
    enabled: false,
    provider: 'openai',
    whisper_model: 'base',
    ollama_model: 'whisper',
    transformers_model: 'openai/whisper-base',
  });
  const [testResult, setTestResult] = useState<any>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/full-config');
      setAIConfig(response.data.ai_config);
      setEscalationRules(response.data.escalation_rules);

      try {
        const sttResponse = await api.get('/speech-to-text/config');
        if (sttResponse.data) {
          setSTTConfig({
            enabled: sttResponse.data.enabled || false,
            provider: sttResponse.data.provider || 'openai',
            openai_api_key: sttResponse.data.openai_api_key,
            groq_api_key: sttResponse.data.groq_api_key,
            anthropic_api_key: sttResponse.data.anthropic_api_key,
            whisper_model: sttResponse.data.whisper_model || 'base',
            ollama_model: sttResponse.data.ollama_model || 'whisper',
            transformers_model: sttResponse.data.transformers_model || 'openai/whisper-base',
          });
        }
      } catch (sttError) {
        console.error('Failed to load STT config:', sttError);
      }
    } catch (error) {
      console.error('Failed to load AI config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    if (!aiConfig) return;
    try {
      setSaving(true);
      await api.put('/ai/config', aiConfig);
      toast({ title: 'AI configuration saved successfully!' });
    } catch (error) {
      console.error('Failed to save AI config:', error);
      toast({ title: 'Failed to save configuration', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEscalationRules = async () => {
    if (!escalationRules) return;
    try {
      setSaving(true);
      await api.put('/ai/escalation-rules', escalationRules);
      toast({ title: 'Escalation rules saved successfully!' });
    } catch (error) {
      console.error('Failed to save escalation rules:', error);
      toast({ title: 'Failed to save escalation rules', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async () => {
    if (!aiConfig) return;
    try {
      setSaving(true);
      setTestResult(null);
      const response = await api.post('/ai/test-provider', {
        llm_provider: aiConfig.llm_provider,
        llm_model: aiConfig.llm_model,
        llm_base_url: aiConfig.llm_base_url,
      });
      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.detail || error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSTT = async () => {
    try {
      setSaving(true);
      const isMasked = (key: string | undefined) =>
        key && (key.includes('...') || key.includes('***'));
      const configToSave = {
        enabled: sttConfig.enabled,
        provider: sttConfig.provider,
        openai_api_key: !isMasked(sttConfig.openai_api_key) ? sttConfig.openai_api_key : undefined,
        groq_api_key: !isMasked(sttConfig.groq_api_key) ? sttConfig.groq_api_key : undefined,
        anthropic_api_key: !isMasked(sttConfig.anthropic_api_key)
          ? sttConfig.anthropic_api_key
          : undefined,
        whisper_model: sttConfig.whisper_model,
        ollama_model: sttConfig.ollama_model,
        transformers_model: sttConfig.transformers_model,
      };
      await api.put('/speech-to-text/config', configToSave);
      toast({ title: 'STT configuration saved successfully!' });
      await loadConfig();
    } catch (error) {
      console.error('Failed to save STT config:', error);
      toast({ title: 'Failed to save STT configuration', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs defaultValue="ai-model">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="ai-model" className="flex items-center gap-1.5">
            <Brain className="h-3.5 w-3.5" />
            AI Model
          </TabsTrigger>
          <TabsTrigger value="escalation" className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5" />
            Escalation Rules
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            Advanced
          </TabsTrigger>
          <TabsTrigger value="stt" className="flex items-center gap-1.5">
            <Mic className="h-3.5 w-3.5" />
            STT Services
          </TabsTrigger>
        </TabsList>

        {/* AI Model Tab */}
        <TabsContent value="ai-model" className="mt-4 space-y-4">
          {aiConfig && (
            <>
              <Alert>
                <Bot className="h-4 w-4" />
                <AlertDescription>
                  AI Configuration uses the LLM settings from Co-Pilot.{' '}
                  <button
                    className="underline text-sm font-medium"
                    onClick={() => (window.location.href = '/settings?section=copilot')}
                  >
                    Configure LLM in Co-Pilot Settings
                  </button>
                </AlertDescription>
              </Alert>

              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Provider &amp; Model</CardTitle>
                  <CardDescription>Read-only — managed in Co-Pilot settings</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Current LLM Provider</Label>
                    <Input value={aiConfig.llm_provider} readOnly className="bg-muted/50" />
                    <p className="text-xs text-muted-foreground">Managed in Co-Pilot settings</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Current Model</Label>
                    <Input value={aiConfig.llm_model} readOnly className="bg-muted/50" />
                    <p className="text-xs text-muted-foreground">Managed in Co-Pilot settings</p>
                  </div>
                  <div className="sm:col-span-2">
                    <Alert
                      variant={aiConfig.api_key_configured ? 'default' : 'destructive'}
                      className={
                        aiConfig.api_key_configured
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : ''
                      }
                    >
                      <AlertDescription>
                        API Key:{' '}
                        {aiConfig.api_key_configured
                          ? 'Configured ✓'
                          : 'Not configured — configure in Co-Pilot settings'}
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Generation Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* System Prompt */}
                  <div className="space-y-1.5">
                    <Label htmlFor="ai-system-prompt">System Prompt</Label>
                    <textarea
                      id="ai-system-prompt"
                      rows={4}
                      value={aiConfig.system_prompt || ''}
                      onChange={(e) =>
                        setAIConfig({ ...aiConfig, system_prompt: e.target.value })
                      }
                      placeholder="You are a helpful customer support assistant…"
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  </div>

                  {/* Temperature */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Temperature</Label>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {aiConfig.temperature}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={2}
                      step={0.1}
                      value={aiConfig.temperature}
                      onChange={(e) =>
                        setAIConfig({ ...aiConfig, temperature: parseFloat(e.target.value) })
                      }
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0 (Focused)</span>
                      <span>1 (Balanced)</span>
                      <span>2 (Creative)</span>
                    </div>
                  </div>

                  {/* Max Tokens + Top P */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="ai-max-tokens">Max Tokens</Label>
                      <Input
                        id="ai-max-tokens"
                        type="number"
                        min={100}
                        max={1000000}
                        value={aiConfig.max_tokens}
                        onChange={(e) =>
                          setAIConfig({ ...aiConfig, max_tokens: parseInt(e.target.value) })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum tokens in response (up to 1M for supported models)
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ai-top-p">Top P</Label>
                      <Input
                        id="ai-top-p"
                        type="number"
                        step={0.1}
                        min={0}
                        max={1}
                        value={aiConfig.top_p}
                        onChange={(e) =>
                          setAIConfig({ ...aiConfig, top_p: parseFloat(e.target.value) })
                        }
                      />
                    </div>
                  </div>

                  {/* Feature Toggles */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Feature Toggles</p>
                    {[
                      {
                        id: 'enable-streaming',
                        key: 'enable_streaming' as const,
                        label: 'Enable Streaming',
                      },
                      {
                        id: 'enable-rag',
                        key: 'enable_rag' as const,
                        label: 'Enable RAG (Knowledge Base)',
                      },
                      {
                        id: 'enable-mcp',
                        key: 'enable_mcp' as const,
                        label: 'Enable MCP Tools',
                      },
                    ].map(({ id, key, label }) => (
                      <div key={id} className="flex items-center gap-2">
                        <Switch
                          id={id}
                          checked={aiConfig[key]}
                          onCheckedChange={(checked) =>
                            setAIConfig({ ...aiConfig, [key]: checked })
                          }
                        />
                        <Label htmlFor={id} className="cursor-pointer">
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  {/* Test + Save */}
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      variant="outline"
                      onClick={handleTestProvider}
                      disabled={saving}
                      className="sm:flex-1"
                    >
                      {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      {saving ? 'Testing…' : 'Test Connection'}
                    </Button>
                    <Button
                      onClick={handleSaveAIConfig}
                      disabled={saving}
                      className="sm:flex-1"
                    >
                      {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {saving ? 'Saving…' : 'Save AI Configuration'}
                    </Button>
                  </div>

                  {testResult && (
                    <Alert
                      variant={testResult.success ? 'default' : 'destructive'}
                      className={
                        testResult.success
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : ''
                      }
                    >
                      <AlertDescription>
                        {testResult.success ? testResult.message : testResult.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Escalation Rules Tab */}
        <TabsContent value="escalation" className="mt-4 space-y-4">
          {escalationRules && (
            <>
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  Configure when conversations should be escalated from AI to human agents.
                </AlertDescription>
              </Alert>

              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Thresholds &amp; Limits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Confidence Threshold */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Confidence Threshold</Label>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {escalationRules.confidence_threshold}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={escalationRules.confidence_threshold}
                      onChange={(e) =>
                        setEscalationRules({
                          ...escalationRules,
                          confidence_threshold: parseFloat(e.target.value),
                        })
                      }
                      className="w-full accent-primary"
                    />
                    <p className="text-xs text-muted-foreground">
                      Escalate when AI confidence is below this threshold
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="max-ai-interactions">Max AI Interactions</Label>
                      <Input
                        id="max-ai-interactions"
                        type="number"
                        value={escalationRules.max_ai_interactions}
                        onChange={(e) =>
                          setEscalationRules({
                            ...escalationRules,
                            max_ai_interactions: parseInt(e.target.value),
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum number of AI messages before escalation
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="min-response-chars">Min Response Characters</Label>
                      <Input
                        id="min-response-chars"
                        type="number"
                        value={escalationRules.min_response_chars}
                        onChange={(e) =>
                          setEscalationRules({
                            ...escalationRules,
                            min_response_chars: parseInt(e.target.value),
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Minimum characters for valid AI response
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Escalation Triggers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Master toggle */}
                  <div className="flex items-start justify-between rounded-lg border p-4 gap-4">
                    <div className="flex-1">
                      <Label
                        htmlFor="auto-escalation"
                        className="font-medium text-sm cursor-pointer"
                      >
                        Auto-Escalation Enabled
                      </Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Master switch to enable/disable all automated escalation triggers. When
                        disabled, only manual escalation will work.
                      </p>
                    </div>
                    <Switch
                      id="auto-escalation"
                      checked={escalationRules.auto_escalation_enabled}
                      onCheckedChange={(checked) =>
                        setEscalationRules({
                          ...escalationRules,
                          auto_escalation_enabled: checked,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-3 pl-1">
                    {[
                      {
                        id: 'confidence-esc',
                        key: 'enable_confidence_escalation' as const,
                        label: 'Low Confidence',
                      },
                      {
                        id: 'sentiment-esc',
                        key: 'enable_sentiment_escalation' as const,
                        label: 'Negative Sentiment',
                      },
                      {
                        id: 'repetition-esc',
                        key: 'enable_repetition_escalation' as const,
                        label: 'Repetitive Responses',
                      },
                      {
                        id: 'explicit-esc',
                        key: 'enable_explicit_request_escalation' as const,
                        label: 'Explicit Human Request',
                      },
                    ].map(({ id, key, label }) => (
                      <div key={id} className="flex items-center gap-2">
                        <Switch
                          id={id}
                          checked={escalationRules[key]}
                          onCheckedChange={(checked) =>
                            setEscalationRules({ ...escalationRules, [key]: checked })
                          }
                          disabled={!escalationRules.auto_escalation_enabled}
                        />
                        <Label
                          htmlFor={id}
                          className={
                            !escalationRules.auto_escalation_enabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer'
                          }
                        >
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5">
                    <Label>Agent Assignment</Label>
                    <Select
                      value={escalationRules.default_assignment_type}
                      onValueChange={(v) =>
                        setEscalationRules({
                          ...escalationRules,
                          default_assignment_type: v as EscalationRules['default_assignment_type'],
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="round_robin">Round Robin</SelectItem>
                        <SelectItem value="load_based">Load Based</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="notify-agents"
                      checked={escalationRules.notify_agents}
                      onCheckedChange={(checked) =>
                        setEscalationRules({ ...escalationRules, notify_agents: checked })
                      }
                    />
                    <Label htmlFor="notify-agents" className="cursor-pointer">
                      Notify agents on escalation
                    </Label>
                  </div>

                  <Button
                    onClick={handleSaveEscalationRules}
                    disabled={saving}
                    className="w-full"
                  >
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {saving ? 'Saving…' : 'Save Escalation Rules'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="mt-4 space-y-4">
          {aiConfig && (
            <>
              <Alert>
                <Settings2 className="h-4 w-4" />
                <AlertDescription>Advanced AI configuration options</AlertDescription>
              </Alert>

              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Response &amp; Sampling</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Response Format</Label>
                      <Select
                        value={aiConfig.response_format}
                        onValueChange={(v) =>
                          setAIConfig({
                            ...aiConfig,
                            response_format: v as AIConfig['response_format'],
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="markdown">Markdown</SelectItem>
                          <SelectItem value="plain">Plain Text</SelectItem>
                          <SelectItem value="html">HTML</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="max-response-length">Max Response Length</Label>
                      <Input
                        id="max-response-length"
                        type="number"
                        value={aiConfig.max_response_length}
                        onChange={(e) =>
                          setAIConfig({
                            ...aiConfig,
                            max_response_length: parseInt(e.target.value),
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum characters in AI response
                      </p>
                    </div>
                  </div>

                  {/* Top P slider */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Top P (Nucleus Sampling)</Label>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {aiConfig.top_p}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={aiConfig.top_p}
                      onChange={(e) =>
                        setAIConfig({ ...aiConfig, top_p: parseFloat(e.target.value) })
                      }
                      className="w-full accent-primary"
                    />
                    <p className="text-xs text-muted-foreground">
                      Controls diversity of responses
                    </p>
                  </div>

                  {/* Thinking mode */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="thinking-mode"
                        checked={aiConfig.enable_thinking_mode}
                        onCheckedChange={(checked) =>
                          setAIConfig({ ...aiConfig, enable_thinking_mode: checked })
                        }
                      />
                      <Label htmlFor="thinking-mode" className="cursor-pointer">
                        Enable Thinking Mode (Extended Thinking)
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-10">
                      Allows AI to show its reasoning process before responding
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveAIConfig}
                    disabled={saving}
                    className="w-full"
                  >
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {saving ? 'Saving…' : 'Save Advanced Settings'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* STT Services Tab */}
        <TabsContent value="stt" className="mt-4 space-y-4">
          <Alert>
            <Mic className="h-4 w-4" />
            <AlertDescription>
              Configure Speech-to-Text service for voice message transcription.
            </AlertDescription>
          </Alert>

          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">STT Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enable toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  id="stt-enabled"
                  checked={sttConfig.enabled}
                  onCheckedChange={(checked) =>
                    setSTTConfig({ ...sttConfig, enabled: checked })
                  }
                />
                <Label htmlFor="stt-enabled" className="cursor-pointer">
                  Enable Speech-to-Text Service
                </Label>
              </div>

              {/* Provider */}
              <div className="space-y-1.5">
                <Label>STT Provider</Label>
                <Select
                  value={sttConfig.provider}
                  onValueChange={(v) =>
                    setSTTConfig({ ...sttConfig, provider: v as STTConfig['provider'] })
                  }
                  disabled={!sttConfig.enabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI Whisper API</SelectItem>
                    <SelectItem value="groq">Groq Whisper</SelectItem>
                    <SelectItem value="whisper">Whisper (Local)</SelectItem>
                    <SelectItem value="ollama">Ollama (Local)</SelectItem>
                    <SelectItem value="transformers">
                      Hugging Face Transformers (Local)
                    </SelectItem>
                    <SelectItem value="anthropic">Anthropic (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Provider-specific fields */}
              {sttConfig.provider === 'openai' && (
                <div className="space-y-1.5">
                  <Label htmlFor="stt-openai-key">OpenAI API Key</Label>
                  <Input
                    id="stt-openai-key"
                    type="password"
                    value={sttConfig.openai_api_key || ''}
                    onChange={(e) =>
                      setSTTConfig({ ...sttConfig, openai_api_key: e.target.value })
                    }
                    disabled={!sttConfig.enabled}
                    placeholder={
                      sttConfig.openai_api_key?.includes('...') ||
                      sttConfig.openai_api_key?.includes('***')
                        ? 'Enter new key to update'
                        : 'sk-…'
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {sttConfig.openai_api_key?.includes('...') ||
                    sttConfig.openai_api_key?.includes('***')
                      ? 'API key is configured (masked). Enter new key to update.'
                      : 'Enter your OpenAI API key for Whisper API access'}
                  </p>
                </div>
              )}

              {sttConfig.provider === 'groq' && (
                <div className="space-y-1.5">
                  <Label htmlFor="stt-groq-key">Groq API Key</Label>
                  <Input
                    id="stt-groq-key"
                    type="password"
                    value={sttConfig.groq_api_key || ''}
                    onChange={(e) =>
                      setSTTConfig({ ...sttConfig, groq_api_key: e.target.value })
                    }
                    disabled={!sttConfig.enabled}
                    placeholder={
                      sttConfig.groq_api_key?.includes('...') ||
                      sttConfig.groq_api_key?.includes('***')
                        ? 'Enter new key to update'
                        : 'gsk_…'
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {sttConfig.groq_api_key?.includes('...') ||
                    sttConfig.groq_api_key?.includes('***')
                      ? 'API key is configured (masked). Enter new key to update.'
                      : 'Enter your Groq API key for Whisper access'}
                  </p>
                </div>
              )}

              {sttConfig.provider === 'anthropic' && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="stt-anthropic-key">Anthropic API Key</Label>
                    <Input
                      id="stt-anthropic-key"
                      type="password"
                      value={sttConfig.anthropic_api_key || ''}
                      onChange={(e) =>
                        setSTTConfig({ ...sttConfig, anthropic_api_key: e.target.value })
                      }
                      disabled={!sttConfig.enabled}
                      placeholder={
                        sttConfig.anthropic_api_key?.includes('...') ||
                        sttConfig.anthropic_api_key?.includes('***')
                          ? 'Enter new key to update'
                          : 'sk-ant-…'
                      }
                    />
                  </div>
                  <Alert variant="destructive">
                    <AlertDescription>
                      Anthropic STT service is not yet available
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {sttConfig.provider === 'whisper' && (
                <div className="space-y-1.5">
                  <Label>Whisper Model</Label>
                  <Select
                    value={sttConfig.whisper_model || 'base'}
                    onValueChange={(v) => setSTTConfig({ ...sttConfig, whisper_model: v })}
                    disabled={!sttConfig.enabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tiny">Tiny (Fastest, Lower Accuracy)</SelectItem>
                      <SelectItem value="base">Base (Balanced)</SelectItem>
                      <SelectItem value="small">Small (Good Quality)</SelectItem>
                      <SelectItem value="medium">Medium (Better Quality)</SelectItem>
                      <SelectItem value="large">Large (Best Quality, Slower)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Local Whisper will download the model on first use
                  </p>
                </div>
              )}

              {sttConfig.provider === 'ollama' && (
                <div className="space-y-1.5">
                  <Label htmlFor="stt-ollama-model">Ollama Model</Label>
                  <Input
                    id="stt-ollama-model"
                    value={sttConfig.ollama_model || 'whisper'}
                    onChange={(e) =>
                      setSTTConfig({ ...sttConfig, ollama_model: e.target.value })
                    }
                    disabled={!sttConfig.enabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    Make sure Ollama is running locally and the model is pulled
                  </p>
                </div>
              )}

              {sttConfig.provider === 'transformers' && (
                <div className="space-y-1.5">
                  <Label htmlFor="stt-transformers-model">Transformers Model</Label>
                  <Input
                    id="stt-transformers-model"
                    value={sttConfig.transformers_model || 'openai/whisper-base'}
                    onChange={(e) =>
                      setSTTConfig({ ...sttConfig, transformers_model: e.target.value })
                    }
                    disabled={!sttConfig.enabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    Hugging Face model ID (e.g., openai/whisper-base, openai/whisper-large-v3).
                    Model will be downloaded from Hugging Face on first use.
                  </p>
                </div>
              )}

              {/* Provider info */}
              <Card className="border-border">
                <CardContent className="pt-4 pb-3">
                  <p className="text-sm font-medium mb-1">Provider Information</p>
                  <p className="text-sm text-muted-foreground">
                    {sttConfig.provider === 'openai' &&
                      'OpenAI Whisper API: Cloud-based, requires API key, fast and accurate'}
                    {sttConfig.provider === 'groq' &&
                      'Groq Whisper: Cloud-based, requires API key, ultra-fast inference'}
                    {sttConfig.provider === 'whisper' &&
                      'Local Whisper: Self-hosted, no API key needed, runs on your server'}
                    {sttConfig.provider === 'ollama' &&
                      'Ollama: Local LLM platform, no API key needed, requires Ollama to be running'}
                    {sttConfig.provider === 'transformers' &&
                      'Hugging Face Transformers: Local ML models, no API key needed, flexible model selection'}
                    {sttConfig.provider === 'anthropic' && 'Anthropic: Coming soon'}
                  </p>
                </CardContent>
              </Card>

              <Button onClick={handleSaveSTT} disabled={saving} className="w-full">
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {saving ? 'Saving…' : 'Save STT Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIConfigPanel;
